import React, { useEffect, useState } from "react";
import AnnouncementForm from "./AnnouncementForm";
import {
  getAllDocsSnap,
  setDocData,
  addNewDoc,
} from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import { deleteDocById } from "../../../utils/firebase/Firebase.utils";
import AnnouncementCard from "./AnnouncementCard";
import PageModal from "../Components/PageModal";
import { motion } from "framer-motion";
import {
  FiPlusCircle,
  FiEdit2,
  FiSearch,
  FiClock,
  FiFilter,
  FiBell,
  FiFileText,
  FiAlertCircle,
  FiActivity,
  FiTrash2,
  FiEye,
  FiInfo,
} from "react-icons/fi";
import { useSelector } from "react-redux";

function Announcements() {
  const links = ["All", "Announcements", "Circulars", "Advisory", "History"];
  const [activeLink, setActiveLink] = React.useState("All");
  const [addMode, setAddMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [announcementArray, setAnnouncementArray] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const currentUser = useSelector((state) => state.userObject);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      await getAllDocsSnap("announcements", (data) => {
        data.sort = (a, b) => {
          return a.createdAt > b.createdAt ? -1 : 1;
        };

        setAnnouncementArray(data);
      });
    };

    const fetchActivityLogs = async () => {
      await getAllDocsSnap("announcement_logs", (data) => {
        data.sort((a, b) => {
          return new Date(b.timestamp) - new Date(a.timestamp);
        });
        setActivityLogs(data);
        setLoading(false);
      });
    };

    fetchAnnouncements();
    fetchActivityLogs();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 rounded-full border-4 border-[#363062] border-t-transparent animate-spin mb-3"></div>
          <p className="text-[#363062] text-sm font-medium">
            Loading announcements...
          </p>
        </div>
      </div>
    );
  }

  const currentItems =
    activeLink === "History"
      ? activityLogs
      : activeLink === "All"
      ? announcementArray
      : announcementArray.filter(
          (announcement) => announcement.type === activeLink.toLowerCase()
        );

  const filteredAnnouncements =
    activeLink === "History"
      ? currentItems.filter((log) =>
          log.announcementTitle.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : currentItems.filter((announcement) =>
          announcement.title.toLowerCase().includes(searchTerm.toLowerCase())
        );

  const handleCreate = () => {
    setAddMode(true);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const logAnnouncementActivity = async (announcement, action) => {
    const logEntry = {
      announcementId: announcement.id,
      announcementTitle: announcement.title,
      announcementType: announcement.type,
      action: action,
      performedBy: currentUser.email,
      timestamp: new Date().toISOString(),
    };

    await addNewDoc("announcement_logs", logEntry);
  };

  const handleToggleStatus = async (announcement) => {
    const newStatus =
      announcement.status === "published" ? "unpublished" : "published";

    await setDocData("announcements", announcement.id, { status: newStatus });

    // Log the activity
    const action = newStatus === "published" ? "published" : "unpublished";
    await logAnnouncementActivity(announcement, action);

    toast.success("Status updated successfully");
  };

  const handleDelete = async (announcement) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      await deleteDocById("announcements", announcement.id);

      // Log the activity
      await logAnnouncementActivity(announcement, "deleted");

      toast.success("Announcement deleted successfully");
    }
  };

  const handleViewMode = (announcement) => {
    setSelectedAnnouncement(announcement);
    setViewMode(true);
  };

  const setAsAdvisory = async (announcement) => {
    await setDocData("announcements", announcement.id, {
      type: "advisory",
    });

    // Log the activity
    await logAnnouncementActivity(announcement, "set as advisory");

    toast.success("Announcement set as advisory");
    setEditMode(false);
  };

  // Animation variants
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { y: 20, opacity: 0 },
    show: { y: 0, opacity: 1 },
  };

  // Format date function
  const formatDate = (timestamp) => {
    if (!timestamp) return "Unknown date";
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    return date.toLocaleString();
  };

  // Get activity icon
  const getActivityIcon = (action) => {
    switch (action) {
      case "created":
        return <FiPlusCircle className="mr-1" />;
      case "published":
        return <FiBell className="mr-1" />;
      case "unpublished":
        return <FiEye className="mr-1" />;
      case "updated":
        return <FiEdit2 className="mr-1" />;
      case "deleted":
        return <FiTrash2 className="mr-1" />;
      case "set as advisory":
        return <FiInfo className="mr-1" />;
      default:
        return <FiActivity className="mr-1" />;
    }
  };

  // Get activity class
  const getActivityClass = (action) => {
    switch (action) {
      case "created":
        return "bg-green-100 text-green-800";
      case "published":
        return "bg-blue-100 text-blue-800";
      case "unpublished":
        return "bg-yellow-100 text-yellow-800";
      case "updated":
        return "bg-indigo-100 text-indigo-800";
      case "deleted":
        return "bg-red-100 text-red-800";
      case "set as advisory":
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "advisory":
        return <FiAlertCircle className="mr-1" />;
      case "circulars":
        return <FiFileText className="mr-1" />;
      default:
        return <FiBell className="mr-1" />;
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 bg-white rounded-xl p-4 shadow-sm border border-gray-100"
        >
          <div className="flex items-center">
            <div className="bg-[#363062] bg-opacity-10 p-2 rounded-lg mr-3">
              <FiBell className="text-[#363062] h-5 w-5" />
            </div>
            <h1 className="text-lg font-medium text-[#363062]">
              Announcements & Advisory
            </h1>
          </div>
          <div className="flex items-center gap-3 mt-3 sm:mt-0">
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={handleCreate}
              className="px-3 py-2 bg-[#8FCD00] text-white rounded-lg flex items-center gap-2 hover:bg-opacity-90 transition-all shadow-sm text-sm font-medium"
            >
              <FiPlusCircle className="h-4 w-4" />
              <span>Create</span>
            </motion.button>
            <motion.button
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setEditMode(!editMode)}
              className={`px-3 py-2 flex items-center gap-2 rounded-lg transition-all shadow-sm text-sm font-medium ${
                editMode
                  ? "bg-[#363062] text-white"
                  : "bg-[#363062] bg-opacity-10 text-[#363062] hover:bg-opacity-20"
              }`}
            >
              <FiEdit2 className="h-4 w-4" />
              <span>{editMode ? "Done" : "Edit"}</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Filter Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm overflow-hidden mb-6 border border-gray-100"
        >
          <div className="flex flex-col md:flex-row md:items-center p-4 gap-4">
            <div className="flex items-center mr-2">
              <FiFilter className="h-4 w-4 text-[#363062] mr-2" />
              <span className="text-sm font-medium text-[#363062]">
                Filter:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {links.map((link, index) => {
                // Define icons for each link
                let icon;
                switch (link) {
                  case "All":
                    icon = <FiActivity className="h-3.5 w-3.5" />;
                    break;
                  case "Announcements":
                    icon = <FiBell className="h-3.5 w-3.5" />;
                    break;
                  case "Circulars":
                    icon = <FiFileText className="h-3.5 w-3.5" />;
                    break;
                  case "Advisory":
                    icon = <FiAlertCircle className="h-3.5 w-3.5" />;
                    break;
                  case "History":
                    icon = <FiClock className="h-3.5 w-3.5" />;
                    break;
                  default:
                    icon = null;
                }

                return (
                  <motion.button
                    key={index}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    onClick={() => setActiveLink(link)}
                    className={`px-3 py-1.5 rounded-lg font-medium transition-all text-xs flex items-center ${
                      activeLink === link
                        ? "bg-[#8FCD00] text-white shadow-sm"
                        : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                    }`}
                  >
                    <span className="mr-1.5">{icon}</span>
                    <span>{link}</span>
                  </motion.button>
                );
              })}
            </div>
            <div className="relative w-full md:w-auto md:min-w-[280px] md:ml-auto">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FiSearch className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="search"
                placeholder={
                  activeLink === "History"
                    ? "Search activity logs"
                    : "Search announcements"
                }
                className="block w-full pl-10 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#363062] focus:border-[#363062] transition-colors"
                onChange={handleSearch}
              />
            </div>
          </div>
        </motion.div>

        {/* Announcements Section */}
        <div>
          {announcementArray.length === 0 && activeLink !== "History" ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-white rounded-xl shadow-sm p-8 text-center border border-gray-100"
            >
              <div className="bg-[#363062] bg-opacity-10 h-16 w-16 flex items-center justify-center rounded-full mx-auto mb-4">
                <FiBell className="h-8 w-8 text-[#363062]" />
              </div>
              <h3 className="mt-1 text-base font-medium text-[#363062]">
                No announcements yet
              </h3>
              <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                Get started by creating a new announcement to share important
                information with your team.
              </p>
              <motion.button
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                onClick={handleCreate}
                className="mt-5 px-4 py-2 bg-[#8FCD00] text-white rounded-lg flex items-center gap-2 mx-auto hover:bg-opacity-90 transition-all text-sm font-medium shadow-sm"
              >
                <FiPlusCircle className="h-4 w-4" />
                <span>Create announcement</span>
              </motion.button>
            </motion.div>
          ) : activeLink === "History" ? (
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100"
            >
              {activityLogs.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="bg-[#363062] bg-opacity-10 h-16 w-16 flex items-center justify-center rounded-full mx-auto mb-4">
                    <FiClock className="h-8 w-8 text-[#363062]" />
                  </div>
                  <h3 className="text-base font-medium text-[#363062]">
                    No activity logs yet
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
                    Activity logs will appear here when announcements are
                    created, published, or updated.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Action
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Title
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Type
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          By
                        </th>
                        <th
                          scope="col"
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          Date & Time
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredAnnouncements.map((log, index) => (
                        <motion.tr
                          key={index}
                          variants={item}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getActivityClass(
                                log.action
                              )} flex items-center`}
                            >
                              {getActivityIcon(log.action)} {log.action}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                              {log.announcementTitle}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span
                              className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full flex items-center ${
                                log.announcementType === "advisory"
                                  ? "bg-blue-100 text-blue-800"
                                  : log.announcementType === "circulars"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-purple-100 text-purple-800"
                              }`}
                            >
                              {getTypeIcon(log.announcementType)}{" "}
                              {log.announcementType}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 truncate max-w-[150px]">
                            {log.performedBy}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(log.timestamp)}
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              variants={container}
              initial="hidden"
              animate="show"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {filteredAnnouncements.length === 0 ? (
                <motion.div
                  variants={item}
                  className="col-span-full bg-white rounded-xl shadow-sm p-8 text-center border border-gray-100"
                >
                  <div className="bg-[#363062] bg-opacity-10 h-14 w-14 flex items-center justify-center rounded-full mx-auto mb-3">
                    <FiSearch className="h-6 w-6 text-[#363062]" />
                  </div>
                  <h3 className="text-base font-medium text-[#363062]">
                    No matching announcements
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Try adjusting your search or filter criteria.
                  </p>
                </motion.div>
              ) : (
                filteredAnnouncements.map((announcement, index) => (
                  <motion.div key={index} variants={item}>
                    <AnnouncementCard
                      announcement={announcement}
                      handleToggleStatus={handleToggleStatus}
                      handleViewMode={handleViewMode}
                      setAsAdvisory={setAsAdvisory}
                      handleDelete={handleDelete}
                      editMode={editMode}
                    />
                  </motion.div>
                ))
              )}
            </motion.div>
          )}
        </div>
      </div>

      {addMode && (
        <PageModal>
          <AnnouncementForm
            setAddMode={setAddMode}
            logAnnouncementActivity={logAnnouncementActivity}
          />
        </PageModal>
      )}

      {viewMode && (
        <PageModal>
          <AnnouncementForm
            setAddMode={setViewMode}
            data={selectedAnnouncement}
            logAnnouncementActivity={logAnnouncementActivity}
          />
        </PageModal>
      )}
    </div>
  );
}

export default Announcements;
